import React, { useState, useRef, useEffect, useCallback } from 'react';
import { MessageSquare, Send, X, Wrench } from 'lucide-react';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import { SlamLoader } from '../../design-system/SlamLoader.jsx';

// Configure marked once
marked.setOptions({ breaks: true, gfm: true });

/**
 * Capture evenly-spaced frames from a video element for Gemini re-analysis.
 * Returns an array of { name, mimeType, base64Data } objects.
 */
async function captureVideoFrames(videoEl, numFrames = 5) {
  if (!videoEl || !videoEl.duration || videoEl.duration <= 0) return [];

  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  canvas.width = 640;
  canvas.height = 360;

  const duration = videoEl.duration;
  const originalTime = videoEl.currentTime;
  const frames = [];

  for (let i = 0; i < numFrames; i++) {
    const time = (i / (numFrames - 1 || 1)) * duration;
    videoEl.currentTime = time;

    await new Promise((resolve) => {
      const onSeeked = () => {
        videoEl.removeEventListener('seeked', onSeeked);
        resolve();
      };
      videoEl.addEventListener('seeked', onSeeked);
      // Timeout fallback in case seeked doesn't fire
      setTimeout(resolve, 500);
    });

    ctx.drawImage(videoEl, 0, 0, canvas.width, canvas.height);
    const base64 = canvas.toDataURL('image/jpeg', 0.7);
    frames.push({
      name: `frame_${i}_t${time.toFixed(1)}s.jpg`,
      mimeType: 'image/jpeg',
      base64Data: base64,
    });
  }

  // Restore video position
  videoEl.currentTime = originalTime;
  return frames;
}

/**
 * AnalysisChat - Follow-up chat about analyzed video content
 *
 * Uses POST /api/video/chat SSE endpoint to let users ask
 * questions about their analyzed video with Gemini 3 Flash re-analysis.
 * Captures multiple frames from the video for visual context.
 *
 * @param {Object} props
 * @param {Array} props.mediaItems - Static media references (e.g., single frame)
 * @param {string} props.analysisResult - Previous analysis text
 * @param {string} props.analysisMode - Sport/analysis mode
 * @param {HTMLVideoElement} props.videoRef - Reference to the video element for frame capture
 */
export function AnalysisChat({ mediaItems = [], analysisResult = '', analysisMode = 'general', videoRef = null, videoUri = '' }) {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [activeToolCall, setActiveToolCall] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const abortRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = useCallback(async () => {
    const question = input.trim();
    if (!question || isStreaming) return;

    setInput('');
    const userMsg = { id: Date.now(), role: 'user', content: question };
    setMessages(prev => [...prev, userMsg]);

    const assistantMsg = { id: Date.now() + 1, role: 'assistant', content: '', isStreaming: true };
    setMessages(prev => [...prev, assistantMsg]);
    setIsStreaming(true);
    setActiveToolCall(null);

    // Abort any prior request
    if (abortRef.current) abortRef.current.abort();
    abortRef.current = new AbortController();

    try {
      // Capture multiple frames from the video for Gemini re-analysis
      let chatMedia = [...mediaItems];
      if (videoRef?.current) {
        const videoFrames = await captureVideoFrames(videoRef.current, 5);
        if (videoFrames.length > 0) {
          chatMedia = videoFrames; // Use fresh multi-frame capture instead of single stale frame
        }
      }

      const chatHistory = messages.map(m => ({ role: m.role, content: m.content }));

      // Build context message with previous analysis for the agent
      const contextPrefix = analysisResult
        ? `[Video Analysis Context]\nMode: ${analysisMode}\nPrevious Analysis:\n${analysisResult.slice(0, 2000)}\n\n`
        : '';

      const resp = await fetch('/api/agent/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: contextPrefix + question,
          history: chatHistory,
          properties: {
            sport: 'basketball',
            videoContext: true,
            videoUri: videoUri || undefined,
            analysisMode,
            files: chatMedia,
          },
        }),
        signal: abortRef.current.signal,
      });

      if (!resp.ok) {
        throw new Error(`Chat failed (${resp.status})`);
      }

      // Parse SSE stream from /api/agent/chat
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let botResponse = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          try {
            const data = JSON.parse(line.slice(6));

            // Accumulate text content (agentChat emits type: 'text' with content field)
            if (data.type === 'text' && data.content) {
              botResponse += data.content;
              setMessages(prev => prev.map(m =>
                m.id === assistantMsg.id ? { ...m, content: botResponse } : m
              ));
            }
            // Also handle 'content' type (contextChat format)
            else if (data.type === 'content' && data.text) {
              botResponse += data.text;
              setMessages(prev => prev.map(m =>
                m.id === assistantMsg.id ? { ...m, content: botResponse } : m
              ));
            }
            // Tool call progress indication
            else if (data.type === 'tool_call') {
              setActiveToolCall(data.tool || data.name || 'processing');
            }
            // Tool result clears progress
            else if (data.type === 'tool_result') {
              setActiveToolCall(null);
            }
            // Error event
            else if (data.type === 'error') {
              botResponse = `Error: ${data.error || data.message || 'Unknown error'}`;
              setMessages(prev => prev.map(m =>
                m.id === assistantMsg.id ? { ...m, content: botResponse, isError: true } : m
              ));
            }
            // Complete / done
            else if (data.type === 'complete' || data.type === 'done') {
              // Stream finished
            }
          } catch { /* skip non-JSON lines */ }
        }
      }

      setMessages(prev => prev.map(m =>
        m.id === assistantMsg.id ? { ...m, isStreaming: false } : m
      ));
    } catch (err) {
      if (err.name === 'AbortError') return;
      setMessages(prev => prev.map(m =>
        m.id === assistantMsg.id
          ? { ...m, content: `Failed to get response: ${err.message}`, isStreaming: false, isError: true }
          : m
      ));
    } finally {
      setIsStreaming(false);
      setActiveToolCall(null);
    }
  }, [input, isStreaming, messages, mediaItems, analysisResult, analysisMode, videoRef, videoUri]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  if (!isOpen) {
    return (
      <button
        onClick={() => { setIsOpen(true); setTimeout(() => inputRef.current?.focus(), 100); }}
        className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-zinc-400 hover:text-white hover:bg-zinc-800/50 transition-colors border border-zinc-800 hover:border-zinc-700"
      >
        <MessageSquare size={14} />
        <span>Ask a follow-up question...</span>
      </button>
    );
  }

  return (
    <div className="border border-zinc-800 rounded-lg overflow-hidden bg-zinc-900/50">
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-zinc-800 bg-zinc-900/80">
        <div className="flex items-center gap-2 text-xs text-zinc-400">
          <MessageSquare size={12} />
          <span>Video Chat</span>
        </div>
        <button
          onClick={() => setIsOpen(false)}
          className="p-1 rounded hover:bg-zinc-800 text-zinc-500 hover:text-white transition-colors"
        >
          <X size={12} />
        </button>
      </div>

      {/* Messages */}
      <div className="max-h-64 overflow-y-auto p-2 space-y-2">
        {messages.length === 0 && (
          <p className="text-xs text-zinc-600 text-center py-4">
            Ask questions about the video — Gemini will re-analyze key frames to answer
          </p>
        )}
        {messages.map((msg) => (
          <div
            key={msg.id}
            className={`text-xs p-2 rounded-lg ${
              msg.role === 'user'
                ? 'bg-amber-500/10 text-amber-200 ml-6'
                : msg.isError
                ? 'bg-red-500/10 text-red-300 mr-6'
                : 'bg-zinc-800/50 text-zinc-300 mr-6'
            }`}
          >
            {msg.role === 'assistant' && !msg.isError ? (
              <div
                className="prose prose-xs max-w-none dark:prose-invert [&_p]:text-xs [&_p]:my-0.5 [&_li]:text-xs [&_h1]:text-sm [&_h2]:text-xs [&_h3]:text-xs [&_strong]:text-zinc-200 [&_code]:text-amber-300 [&_code]:text-xs"
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(marked(msg.content || '')) }}
              />
            ) : (
              <div className="whitespace-pre-wrap">{msg.content}</div>
            )}
            {msg.isStreaming && activeToolCall && (
              <div className="flex items-center gap-1 mt-1 text-[10px] text-amber-400/70">
                <Wrench size={10} className="animate-spin" />
                <span>{activeToolCall}</span>
              </div>
            )}
            {msg.isStreaming && (
              <SlamLoader size={10} variant="dots" />
            )}
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="flex items-center gap-2 p-2 border-t border-zinc-800">
        <input
          ref={inputRef}
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Ask about the video..."
          disabled={isStreaming}
          className="flex-1 bg-zinc-800/50 text-sm text-white placeholder-zinc-600 rounded px-3 py-1.5 outline-none focus:ring-1 focus:ring-amber-500/30 disabled:opacity-50"
        />
        <button
          onClick={sendMessage}
          disabled={!input.trim() || isStreaming}
          className="p-1.5 rounded bg-amber-500/20 text-amber-400 hover:bg-amber-500/30 disabled:opacity-30 transition-colors"
        >
          <Send size={14} />
        </button>
      </div>
    </div>
  );
}

export default AnalysisChat;
